import React, { useMemo, useRef, useState } from 'react';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import {
  EmptyContent,
  ErrorHandler,
  Flex,
  Gap,
  Gutter,
  LoadingWrapper,
  Placeholder,
  SideShell,
  Tabs,
} from '@common/components';
import { useMutation, useNavigate, useParams, useQuery } from '@common/hooks';
import { createMessage } from 'works/legacy/lib/message-helper';
import {
  ScheduleAiSummary,
  ScheduleCalendar,
  ScheduleHealthStrip,
  ScheduleNeedsAttention,
  ScheduleReviewActivityTab,
  ScheduleReviewSessionHeader,
  ScheduleReviewTeamTab,
  ScheduleShiftDetailPanel,
  ScheduleWorkerProfilePanel,
  ScheduleWorkerProfileUpdatePanel,
} from '@load-balancing/components';
import {
  applyShiftOverrides,
  getAssignedShiftIdsForMember,
  getAvailableShiftIdsForMember,
  needsAttentionDismissId,
  teamFteFilterForItem,
} from '@load-balancing/services';
import {
  SCHEDULE_REVIEW_QUERY,
  type ScheduleReviewNeedsAttentionType,
} from '@load-balancing/graphql/queries/schedule-review';
import { DECIDE_SCHEDULE_REVIEW_UNIT_MUTATION } from '@load-balancing/graphql/mutations/decide-schedule-review-unit';
import { ASSIGN_SHIFT_MUTATION } from '@load-balancing/graphql/mutations/assign-shift';
import { UNASSIGN_SHIFT_MUTATION } from '@load-balancing/graphql/mutations/unassign-shift';
import type {
  ScheduleDayType,
  ScheduleDemandType,
  ScheduleMoveSourceFilterType,
  ScheduleRoleType,
  ScheduleShiftDecisionType,
  ScheduleShiftOverrideType,
  ScheduleShiftType,
  ScheduleShiftTypeFilterType,
  ScheduleShiftTypeSectionKeyType,
  ScheduleShiftTypeSectionType,
  ScheduleTeamFteFilterType,
  ScheduleTeamMemberType,
  ScheduleWeekStatusType,
} from '@load-balancing/types';
import {
  MOCK_DAYS_BY_WEEK,
  MOCK_SHIFTS_BY_WEEK,
  MOCK_SHIFT_TYPE_SECTIONS,
} from '@load-balancing/mock/schedule-calendar-mock';
import { MOCK_SCHEDULE_MOVES } from '@load-balancing/mock/schedule-activity-mock';
import {
  OPEN_SHIFT_CANDIDATES_QUERY,
  SHIFT_CANDIDATES_QUERY,
} from '@load-balancing/graphql/queries/shift-candidates';
import {
  assignedHighlightWeekKey,
  assignErrorMessage,
  cellCoverage,
  findShiftWeekKey,
  parseOpenShiftId,
  REFETCH_AFTER_ASSIGN,
  REFETCH_AFTER_UNASSIGN,
  toDemand,
  toShift,
  toTeamMember,
  unassignErrorMessage,
  WEEK_STATUS_BY_BACKEND,
} from './_schedule-review-page-service';
import { useScheduleReviewPageDragDrop } from './_schedule-review-page-drag-drop-hook';

// TODO(WORK-6031): replace the remaining Review tab placeholder with the real panel.

// TODO(WORK-6016): The Activity tab is mocked today (MOCK_SCHEDULE_MOVES / MOCK_SHIFTS_BY_WEEK
// and friends) and is hidden from the UI until it is fully implemented against real data in
// WORK-6016. Flip this flag back to `true` to restore the tab once that work lands.
const SHOW_ACTIVITY_TAB = false;

/**
 * Read-only scheduling grid (UI Phase 1). The session header, shift-type bands,
 * role pills and the full Sun–Sat weeks (day columns, placed shifts, per-week
 * status + pendingCount) all come from the `scheduleReview` query, keyed on the
 * route's balancer run + organization unit. The Team tab renders the real roster
 * from `scheduleReview.team`. "Review & Mark Complete" approves the unit via the
 * `decideScheduleReviewUnit` mutation and returns to the dashboard; the Activity
 * tab still runs on mock data (WORK-6007).
 */
export function ScheduleReviewPage() {
  const { serviceLineId, runId, organizationUnitId } = useParams<{
    serviceLineId: string;
    runId: string;
    organizationUnitId: string;
  }>();
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(SCHEDULE_REVIEW_QUERY, {
    variables: {
      balancerRunId: runId ?? '',
      organizationUnitId: organizationUnitId ?? '',
    },
    skip: !runId || !organizationUnitId,
  });

  const [activeWeekKey, setActiveWeekKey] = useState<string | undefined>();
  const [roleFilter, setRoleFilter] = useState<ScheduleRoleType[]>([]);
  const [shiftTypeFilter, setShiftTypeFilter] =
    useState<ScheduleShiftTypeFilterType>('all');
  // "Review & Mark Complete" approves the unit in view; on success the run advances
  // (and completes once no unit is left pending) and we return to the dashboard.
  const [decideUnit, { loading: isSaving }] = useMutation(
    DECIDE_SCHEDULE_REVIEW_UNIT_MUTATION,
    {
      onCompleted: () => navigate('/works/schedule/dashboard'),
      onError: () =>
        createMessage(
          'error',
          'Could not mark this unit complete. Please try again.'
        ),
    }
  );

  const handleMarkComplete = () => {
    if (!runId || !organizationUnitId) return;
    decideUnit({
      variables: {
        balancerRunId: runId,
        organizationUnitId,
        decision: 'APPROVED',
      },
    });
  };

  // Manual assign/unassign (WORK-6384): the Shift Detail panel buttons persist via these editor
  // mutations (each with its own toast), then the recompute-on-read feeds refetch. Drag-to-assign
  // (WORK-6399) is owned by `useScheduleReviewPageDragDrop` below, which has its own toast-silent,
  // rejection-driven mutations.
  // WORK-6505 (QA): the in-flight panel unassign — the uuid being removed (`fromShiftId`) and the
  // freed cell's open-row id to re-point to (`toOpenCellId`). Stashed by handleUnassign so the
  // unassign onCompleted re-opens the panel on the now-open slot, and so `selectedShift` can bridge
  // the one-render gap where the uuid is gone but the selection hasn't moved yet. Scoped to that
  // exact uuid (see selectedShift) so it never leaks into other flows like drag-and-drop.
  const pendingUnassignRef = useRef<{
    fromShiftId: string;
    toOpenCellId: string;
  } | null>(null);
  const [assignShift, { loading: isAssigning }] = useMutation(
    ASSIGN_SHIFT_MUTATION,
    {
      refetchQueries: REFETCH_AFTER_ASSIGN,
      awaitRefetchQueries: true,
      onCompleted: (data) => {
        createMessage('success', 'Worker assigned.');
        // Keep the panel open on the freshly-filled shift so the scheduler sees the worker
        // land in both the panel and the grid, rather than the panel closing when the
        // open-slot id it was keyed on disappears. The awaited refetch already has the new row.
        const assignedId = data.assignShift?.affectedShiftId;
        if (assignedId) setSelectedShiftId(assignedId);
      },
      onError: (mutationError) =>
        createMessage('error', assignErrorMessage(mutationError)),
    }
  );
  const [unassignShift, { loading: isUnassigning }] = useMutation(
    UNASSIGN_SHIFT_MUTATION,
    {
      refetchQueries: REFETCH_AFTER_UNASSIGN,
      awaitRefetchQueries: true,
      onCompleted: () => {
        createMessage('success', 'Worker unassigned.');
        // Re-open the panel on the now-open slot so the scheduler can immediately reassign,
        // instead of closing when the removed shift's uuid vanishes from the refetched grid.
        if (pendingUnassignRef.current) {
          setSelectedShiftId(pendingUnassignRef.current.toOpenCellId);
          pendingUnassignRef.current = null;
        }
      },
      onError: (mutationError) => {
        pendingUnassignRef.current = null;
        createMessage('error', unassignErrorMessage(mutationError));
      },
    }
  );
  // Selecting an activity move cross-highlights its target shift in the grid.
  const [selectedMoveId, setSelectedMoveId] = useState<string | null>(null);
  // Side panel tab + the Activity tab's source filter. "View auto-applied changes"
  // opens the Activity tab filtered to Works (the balancer's auto-applied moves).
  const [activeSideTab, setActiveSideTab] = useState('review');
  const [activitySourceFilter, setActivitySourceFilter] =
    useState<ScheduleMoveSourceFilterType>('all');
  // Team-tab FTE filter, controlled here so a Needs Attention FTE card can open
  // the Team tab pre-filtered to Above/Below FTE.
  const [teamFteFilter, setTeamFteFilter] =
    useState<ScheduleTeamFteFilterType>('all');
  // Team-tab worker-id filter: a "View in Team" Needs Attention card limits the roster
  // to exactly the staff it counted (by uuid). Controlled here so the card can set it.
  const [teamWorkerIds, setTeamWorkerIds] = useState<string[]>([]);
  // The single "Show on Schedule" Needs Attention selection whose shifts are ringed
  // in the grid (keyed by the card's dismiss-id so only one card can be active).
  const [scheduleSelection, setScheduleSelection] = useState<{
    key: string;
    shiftIds: string[];
  } | null>(null);
  // Side panel collapse state (open by default), controlled so actions like
  // "View auto-applied changes" can re-open it when collapsed.
  const [sideCollapsed, setSideCollapsed] = useState(false);
  // Team tab "Show available shifts": the worker whose open shifts are ringed in the grid.
  const [highlightedWorkerId, setHighlightedWorkerId] = useState<string | null>(
    null
  );
  // Team tab: the worker whose assigned shifts are filled in the grid.
  const [assignedHighlightWorkerId, setAssignedHighlightWorkerId] = useState<
    string | null
  >(null);
  // Team tab: the worker whose profile panel is open over the side tabs.
  const [profileWorkerId, setProfileWorkerId] = useState<string | null>(null);
  // The worker whose "Update Profile" form is open, layered over the profile panel.
  const [updateProfileWorkerId, setUpdateProfileWorkerId] = useState<
    string | null
  >(null);
  // The scheduled change being edited (null = creating a new change).
  const [updateProfileTermId, setUpdateProfileTermId] = useState<string | null>(
    null
  );
  // Grid: the shift whose detail panel is open over the side tabs (a takeover).
  const [selectedShiftId, setSelectedShiftId] = useState<string | null>(null);
  // Open rows are synthesized with the id `open:<week>:<day>:<band>:<role>:<idx>`
  // and no backing shift uuid, so their candidates come from the sibling
  // `openShiftCandidates(week/day/band/role)` field rather than `shiftCandidates`.
  // Parse the descriptor straight off the id (the only carrier before the grid
  // pipeline runs) so the two candidate hooks stay ahead of the early returns below.
  const openCell = parseOpenShiftId(selectedShiftId);
  // Recompute-on-read candidate feed for an assigned shift (real uuid). A second
  // query on the same `scheduleReview` resolver (Apollo cache-merges); resolves only
  // while a non-open shift is selected. `shiftCandidates` is null for an unknown id.
  const { data: candidatesData, loading: candidatesLoading } = useQuery(
    SHIFT_CANDIDATES_QUERY,
    {
      variables: {
        balancerRunId: runId ?? '',
        organizationUnitId: organizationUnitId ?? '',
        shiftId: selectedShiftId ?? '',
      },
      skip: !runId || !organizationUnitId || !selectedShiftId || !!openCell,
    }
  );
  // The same recompute-on-read feed for an OPEN demand cell, keyed by the grid
  // descriptor instead of a shift uuid; resolves only while an open row is selected.
  const { data: openCandidatesData, loading: openCandidatesLoading } = useQuery(
    OPEN_SHIFT_CANDIDATES_QUERY,
    {
      variables: {
        balancerRunId: runId ?? '',
        organizationUnitId: organizationUnitId ?? '',
        weekKey: openCell?.weekKey ?? '',
        dayKey: openCell?.dayKey ?? '',
        shiftTypeSection: openCell?.shiftTypeSection ?? '',
        role: openCell?.role ?? '',
      },
      skip: !runId || !organizationUnitId || !openCell,
    }
  );
  // An open cell reads from the open field; every other shift from the uuid field.
  const candidateBuckets = openCell
    ? (openCandidatesData?.scheduleReview?.openShiftCandidates ?? null)
    : (candidatesData?.scheduleReview?.shiftCandidates ?? null);
  const candidatesPending = openCell
    ? openCandidatesLoading
    : candidatesLoading;
  // In-session assignment edits keyed by shift id — manual assign / unassign and Works-suggestion
  // Accept/Reject. Stands in for the balancer move mutations (WORK-6015) until they land.
  const [shiftOverrides, setShiftOverrides] = useState<
    Record<string, ScheduleShiftOverrideType>
  >({});
  // Role/shift-type filters snapshotted before a worker highlight expanded the grid,
  // restored when the highlight is cleared so available shifts are never hidden behind them.
  const preHighlightFiltersRef = useRef<{
    roleFilter: ScheduleRoleType[];
    shiftTypeFilter: ScheduleShiftTypeFilterType;
  } | null>(null);

  useSetBreadcrumbs([
    {
      name: 'Schedule',
      path: '/works/schedule',
    },
    {
      name: 'Dashboard',
      path: '/works/schedule/dashboard',
    },
    {
      name: data?.scheduleReview?.unitName,
      path: `/works/schedule/dashboard/${serviceLineId}/review/${runId}/${organizationUnitId}`,
    },
  ]);

  const review = data?.scheduleReview;
  const weeks = review?.weeks ?? [];

  // The run service line's cross-role EligibilityRule matrix as a lookup Set (WORK-6513) — fed to the
  // drag ring and the "Eligible shifts" ring so both honor the same rules the engine + candidate feed
  // apply. Built once from the string list; empty when the payload is absent (safe under-scope).
  const eligibleRolePairs = useMemo(
    () => new Set(review?.eligibleRolePairs ?? []),
    [review?.eligibleRolePairs]
  );

  // Mark Complete shows only while the run awaits review AND this unit is still
  // PENDING — the run stays awaiting_review until every unit is decided, so gate
  // per-unit (APPROVED/REJECTED, or a phase-less legacy run, hide the action).
  const canDecideUnit =
    !!review?.awaitingReview && review.unitDecision === 'PENDING';

  // Keep the selected week valid as data loads / changes; default to the first week.
  const effectiveWeekKey =
    activeWeekKey && weeks.some((week) => week.key === activeWeekKey)
      ? activeWeekKey
      : weeks[0]?.key;
  const activeWeekIndex = weeks.findIndex(
    (week) => week.key === effectiveWeekKey
  );
  const activeWeek = weeks[activeWeekIndex];

  const selectWeekByOffset = (offset: number) => {
    const target = weeks[activeWeekIndex + offset];
    if (target) setActiveWeekKey(target.key);
  };

  // The backend emits only the canonical day/night band + icon keys, so the open
  // string fields narrow to the FE section type.
  const shiftTypeSections: ScheduleShiftTypeSectionType[] = (
    review?.shiftTypeSections ?? []
  ).map((section) => ({
    key: section.key as ScheduleShiftTypeSectionKeyType,
    label: section.label,
    time: section.time,
    icon: section.icon as ScheduleShiftTypeSectionType['icon'],
  }));
  const roles: ScheduleRoleType[] = review?.roles ?? [];
  // The run's configured charge role (data-driven): the profile panels use it so charge
  // eligibility keys off the real charge role, never a hardcoded "Charge" abbreviation.
  const chargeRole = review?.chargeRole ?? undefined;
  // Scopes the AI summary's persisted collapse/dismiss to this (run, unit,
  // generation). The generatedAt segment resets the choice when the summary is
  // regenerated; passing it as the React key too remounts the card on unit switch
  // so one unit's dismissed/collapsed state never carries onto another.
  const aiSummaryStorageKey = review?.aiSummary
    ? `lb-ai-summary:${runId}:${organizationUnitId}:${review.aiSummary.generatedAt ?? ''}`
    : '';
  // The real active home-unit roster (Team tab + worker profile panel). Identity
  // is populated; hour/weekend metrics are null until later changes land.
  const members: ScheduleTeamMemberType[] = (review?.team ?? []).map(
    toTeamMember
  );

  const weekStatuses: ScheduleWeekStatusType[] = weeks.map((week) => ({
    key: week.key,
    label: week.label,
    status: WEEK_STATUS_BY_BACKEND[week.status],
    pendingCount: week.pendingCount,
    hasCoverageGap: week.hasCoverageGap ?? undefined,
  }));
  // The GraphQL day shape is structurally the calendar's ScheduleDayType (plus an
  // inert __typename the grid never reads), so it flows straight in — no mapper.
  const days: ScheduleDayType[] = activeWeek?.days ?? [];
  const shifts: ScheduleShiftType[] = (activeWeek?.shifts ?? []).map(toShift);
  // Layer in-session assignment edits (panel + drag-to-assign) onto the grid shifts so the calendar
  // reflects manual assigns/unassigns and accepted suggestions.
  const effectiveShifts = applyShiftOverrides(shifts, shiftOverrides, members);

  // Drag-to-assign owns its own state, mutations and the optimistic overlay (WORK-6399); it reads
  // the live roster + override-layered shifts and writes back through the shared override setter.
  const {
    isDragMutating,
    handleWorkerDragStart,
    handleWorkerDragEnd,
    handleGridDragStart,
    getDropValidity,
    handleDropAssign,
  } = useScheduleReviewPageDragDrop({
    runId,
    organizationUnitId,
    members,
    effectiveShifts,
    days,
    eligibleRolePairs,
    shiftOverrides,
    setShiftOverrides,
    // Keep the Shift Detail panel open when a drop fills the slot it was showing: re-point it
    // from the (now-gone) open-row id to the inserted shift. Only when the panel was on that
    // target — a drag from elsewhere (e.g. the Team tab) must not pop the panel open.
    onAssigned: (toShiftId, affectedShiftId) =>
      setSelectedShiftId((prev) =>
        prev === toShiftId ? affectedShiftId : prev
      ),
  });

  // `useScheduleReviewPageDragDrop` is the last hook, so the data-error short-circuit runs after it
  // (the derivations above degrade to empty arrays when `data` is absent, so the hook is inert).
  if (error) {
    return <ErrorHandler error={error} log="ScheduleReviewPage" />;
  }

  const demands: ScheduleDemandType[] = (activeWeek?.demands ?? []).map(
    toDemand
  );
  // Every week's shifts/days keyed by week, for the worker-availability lookup —
  // it scans all weeks to ring a worker's open shifts and snap to the first one.
  const shiftsByWeek: Record<string, ScheduleShiftType[]> = Object.fromEntries(
    weeks.map((week) => [week.key, week.shifts.map(toShift)])
  );
  const daysByWeek: Record<string, ScheduleDayType[]> = Object.fromEntries(
    weeks.map((week) => [week.key, week.days])
  );

  // Workers with at least one assigned shift anywhere in the schedule under review — drives
  // the Team tab's "Assigned shifts" toggle so it only shows for workers it can ring.
  const assignedWorkerIds = Array.from(
    new Set(
      Object.values(shiftsByWeek)
        .flat()
        .map((shift) => shift.assignedWorkerId)
        .filter((id): id is string => Boolean(id))
    )
  );

  // Workers with at least one eligible open shift anywhere in the schedule — drives the Team tab's
  // "Eligible shifts" toggle so it only shows for workers it can actually ring/navigate to (a
  // below-FTE roster worker with no matching open slot would otherwise get a no-op toggle).
  const eligibleWorkerIds = members
    .filter(
      (member) =>
        getAvailableShiftIdsForMember(member, shiftsByWeek, daysByWeek, {
          eligibleRolePairs,
        }).length > 0
    )
    .map((member) => member.id);

  const selectedMove = MOCK_SCHEDULE_MOVES.find(
    (move) => move.id === selectedMoveId
  );
  // The open Shift Detail panel keeps its shift ringed; otherwise the highlight follows the
  // selected activity move's target/origin shift.
  const highlightedShiftId =
    selectedShiftId ??
    selectedMove?.toShiftId ??
    selectedMove?.fromShiftId ??
    null;
  // Grid cells a "Show on Schedule" Needs Attention card rings (fatigue/unfilled).
  const attentionShiftIds = scheduleSelection?.shiftIds ?? [];

  const highlightedMember = members.find(
    (member) => member.id === highlightedWorkerId
  );
  const availableShiftIds = highlightedMember
    ? getAvailableShiftIdsForMember(
        highlightedMember,
        shiftsByWeek,
        daysByWeek,
        { eligibleRolePairs }
      )
    : [];

  const assignedHighlightMember = members.find(
    (member) => member.id === assignedHighlightWorkerId
  );
  const assignedShiftIds = assignedHighlightMember
    ? getAssignedShiftIdsForMember(assignedHighlightMember, shiftsByWeek)
    : [];

  const profileMember =
    members.find((member) => member.id === profileWorkerId) ?? null;

  // The selected shift (and its day/band) is resolved from the active week — the clicked card
  // always lives there. A week change orphans the selection, which the panel simply stops rendering.
  // During an unassign the selected uuid is deleted from the refetched grid one render before
  // `onCompleted` re-points the selection to the freed open slot. Bridge that one frame by
  // resolving to the pending open cell — but ONLY when the current selection is that exact uuid,
  // so the bridge can never select a stale cell in unrelated flows (e.g. a drag-and-drop that
  // empties the selected cell). The panel stays mounted and just swaps content (filled → open).
  const pendingUnassign = pendingUnassignRef.current;
  const selectedShift = selectedShiftId
    ? (effectiveShifts.find((shift) => shift.id === selectedShiftId) ??
      (pendingUnassign?.fromShiftId === selectedShiftId
        ? (effectiveShifts.find(
            (shift) => shift.id === pendingUnassign.toOpenCellId
          ) ?? null)
        : null))
    : null;
  const selectedDay = selectedShift
    ? days.find((day) => day.key === selectedShift.dayKey)
    : undefined;
  const selectedSection = selectedShift
    ? shiftTypeSections.find(
        (section) => section.key === selectedShift.shiftTypeSection
      )
    : undefined;
  const selectedOverride = selectedShiftId
    ? (shiftOverrides[selectedShiftId] ?? {})
    : {};
  // Coverage base for the open-slot KPI chips (filled vs planned for the cell); the panel
  // turns it into a per-candidate before→after delta.
  const selectedCoverage = selectedShift
    ? cellCoverage(selectedShift, effectiveShifts, demands)
    : null;

  // Activity selection: highlight the move's target shift and snap the grid to
  // its week so the highlighted card is visible (the grid then centers on it).
  // Selecting a move and highlighting a worker are mutually exclusive grid states.
  const handleMoveSelect = (moveId: string | null) => {
    setSelectedMoveId(moveId);
    if (moveId) clearWorkerHighlight();
    setScheduleSelection(null);
    if (!moveId) return;

    const move = MOCK_SCHEDULE_MOVES.find((entry) => entry.id === moveId);
    const shiftId = move?.toShiftId ?? move?.fromShiftId;
    const weekKey = shiftId
      ? findShiftWeekKey(shiftId, MOCK_SHIFTS_BY_WEEK)
      : null;
    if (weekKey) setActiveWeekKey(weekKey);
  };

  const clearWorkerHighlight = () => {
    setHighlightedWorkerId(null);
    if (preHighlightFiltersRef.current) {
      setRoleFilter(preHighlightFiltersRef.current.roleFilter);
      setShiftTypeFilter(preHighlightFiltersRef.current.shiftTypeFilter);
      preHighlightFiltersRef.current = null;
    }
  };

  // Team tab toggle: ring the worker's open shifts, expand the grid filters so none are
  // hidden, and snap to the week of their first available shift. Re-clicking clears it.
  const handleHighlightWorker = (workerId: string | null) => {
    if (!workerId) {
      clearWorkerHighlight();
      return;
    }

    setSelectedMoveId(null);
    setScheduleSelection(null);
    if (!preHighlightFiltersRef.current) {
      preHighlightFiltersRef.current = { roleFilter, shiftTypeFilter };
    }
    setRoleFilter([]);
    setShiftTypeFilter('all');
    setHighlightedWorkerId(workerId);

    const member = members.find((entry) => entry.id === workerId);
    const firstShiftId = member
      ? getAvailableShiftIdsForMember(member, shiftsByWeek, daysByWeek, {
          eligibleRolePairs,
        })[0]
      : undefined;
    const weekKey = firstShiftId
      ? findShiftWeekKey(firstShiftId, shiftsByWeek)
      : null;
    if (weekKey) setActiveWeekKey(weekKey);
  };

  // Team tab toggle: fill the worker's assigned shifts in the grid and, unless the current
  // week already shows one, snap to the earliest week that has one — mirroring the eligible
  // toggle so the fills are always visible regardless of the week on screen. Re-clicking clears it.
  const handleHighlightAssigned = (workerId: string | null) => {
    if (!workerId) {
      setAssignedHighlightWorkerId(null);
      return;
    }

    setAssignedHighlightWorkerId(workerId);

    const member = members.find((entry) => entry.id === workerId);
    const assignedShiftIds = member
      ? getAssignedShiftIdsForMember(member, shiftsByWeek)
      : [];
    const weekKey = assignedHighlightWeekKey(
      assignedShiftIds,
      shiftsByWeek,
      effectiveWeekKey
    );
    if (weekKey) setActiveWeekKey(weekKey);
  };

  // Team tab: clicking a worker name opens their profile as a side-panel takeover.
  const handleWorkerSelect = (workerId: string) => {
    setProfileWorkerId(workerId);
  };

  // Needs Attention CTAs. "View in Team" limits the Team tab to exactly the staff the
  // card counted (by uuid); "Show on Schedule" rings the cells it points at and scrolls
  // to the first (the grid centers on it). Both clear any competing grid highlight.
  const handleNeedsAttentionAction = (
    item: ScheduleReviewNeedsAttentionType
  ) => {
    if (item.action === 'VIEW_IN_TEAM') {
      setTeamWorkerIds(item.targetWorkerUuids);
      setTeamFteFilter(teamFteFilterForItem(item));
      setActiveSideTab('team');
      setSideCollapsed(false);
    } else if (item.action === 'SHOW_ON_SCHEDULE') {
      const key = needsAttentionDismissId(item, effectiveWeekKey);
      if (scheduleSelection?.key === key) {
        setScheduleSelection(null);
      } else {
        setSelectedMoveId(null);
        clearWorkerHighlight();
        setScheduleSelection({ key, shiftIds: item.targetShiftIds });
      }
    }
  };

  // Dismissing the card whose shifts are currently shown clears the grid highlight.
  const handleNeedsAttentionDismiss = (
    item: ScheduleReviewNeedsAttentionType
  ) => {
    if (
      scheduleSelection?.key === needsAttentionDismissId(item, effectiveWeekKey)
    ) {
      setScheduleSelection(null);
    }
  };

  // Grid: clicking a shift card opens its detail panel as a takeover over the side tabs,
  // expanding the side section when it was collapsed so the panel is visible.
  const handleShiftSelect = (shiftId: string) => {
    setSelectedShiftId(shiftId);
    setScheduleSelection(null);
    setSideCollapsed(false);
  };

  const patchShiftOverride = (
    shiftId: string,
    patch: ScheduleShiftOverrideType
  ) => {
    setShiftOverrides((prev) => ({
      ...prev,
      [shiftId]: { ...prev[shiftId], ...patch },
    }));
  };

  // Shift Detail actions — persisted via the manual-move mutations (WORK-6384). Assign is
  // open-slot-only (reassign deferred): the slot descriptor comes from the selected open
  // cell's id. Unassign clears a filled shift by its uuid. Both refetch on success.
  const handleAssign = (memberId: string) => {
    // Bail while a mutation is in flight so a double-click can't fire a second assign
    // against the same open cell (the server rejects the duplicate, but don't send it).
    if (!openCell || !runId || !organizationUnitId || isAssigning) return;
    assignShift({
      variables: {
        balancerRunId: runId,
        organizationUnitId,
        weekKey: openCell.weekKey,
        dayKey: openCell.dayKey,
        shiftTypeSection: openCell.shiftTypeSection,
        role: openCell.role,
        workerId: memberId,
      },
    });
  };

  const handleUnassign = () => {
    if (
      !selectedShiftId ||
      openCell ||
      !runId ||
      !organizationUnitId ||
      isUnassigning
    )
      return;
    // Stash the uuid being removed + the freed cell's synthesized open-row id, so onCompleted can
    // keep the panel open on it (index 0 always exists once the slot reopens; over-staffed cells
    // fall back to close). Scoped to `fromShiftId` so the selectedShift bridge can't leak elsewhere.
    pendingUnassignRef.current = selectedShift
      ? {
          fromShiftId: selectedShiftId,
          toOpenCellId: `open:${effectiveWeekKey}:${selectedShift.dayKey}:${selectedShift.shiftTypeSection}:${selectedShift.role}:0`,
        }
      : null;
    unassignShift({
      variables: {
        balancerRunId: runId,
        organizationUnitId,
        shiftId: selectedShiftId,
      },
    });
  };

  const handleSuggestionDecision = (decision: ScheduleShiftDecisionType) => {
    if (selectedShiftId) {
      patchShiftOverride(selectedShiftId, { suggestionDecision: decision });
    }
  };

  // Session-wide count of balancer-auto-filled shifts, mirroring the per-week badges
  // in the grid. The MVP applies the balancer's moves automatically (no review step),
  // so these are surfaced as "auto-filled" rather than suggestions to review.
  const autoFilledCount = weeks.reduce(
    (sum, week) => sum + week.pendingCount,
    0
  );

  const sidePanels = {
    'shift-detail': selectedShift ? (
      <ScheduleShiftDetailPanel
        shift={selectedShift}
        dayLabel={selectedDay?.dayLabel ?? ''}
        dateLabel={selectedDay?.dateLabel ?? ''}
        bandLabel={selectedSection?.label ?? ''}
        bandTime={selectedSection?.time ?? ''}
        unitName={review?.unitName}
        team={members}
        buckets={candidateBuckets}
        candidatesLoading={candidatesPending}
        coverage={selectedCoverage}
        override={selectedOverride}
        assigning={isAssigning}
        unassigning={isUnassigning}
        onAssign={handleAssign}
        onUnassign={handleUnassign}
        onDecision={handleSuggestionDecision}
        onWorkerSelect={handleWorkerSelect}
        onWorkerDragStart={handleWorkerDragStart}
        onWorkerDragEnd={handleWorkerDragEnd}
      />
    ) : null,
    'worker-profile': profileMember ? (
      <ScheduleWorkerProfilePanel
        member={profileMember}
        chargeRole={chargeRole}
        schedulePeriodId={review?.schedulePeriodId}
        onUpdateProfile={(changeId) => {
          setUpdateProfileWorkerId(profileMember.id);
          setUpdateProfileTermId(changeId ?? null);
        }}
      />
    ) : null,
    'worker-profile-update': updateProfileWorkerId ? (
      <ScheduleWorkerProfileUpdatePanel
        workerId={updateProfileWorkerId}
        editingTermId={updateProfileTermId}
        schedulePeriodId={review?.schedulePeriodId}
        assignableRoles={roles}
        chargeRole={chargeRole}
        onClose={() => {
          setUpdateProfileWorkerId(null);
          setUpdateProfileTermId(null);
        }}
      />
    ) : null,
  };

  // Panel stack: the shift detail opens over the tabs; a worker-profile click inside it (or from
  // the Team tab) layers on top, and "Update Profile" layers the edit form over that. Back/Esc
  // pops the top panel and clears the matching selection.
  const activePanelStack = [
    selectedShift ? 'shift-detail' : null,
    profileWorkerId ? 'worker-profile' : null,
    updateProfileWorkerId ? 'worker-profile-update' : null,
  ].filter((key): key is string => key !== null);

  // Back/Esc pops the top panel; clear whichever selection has left the active stack.
  const handleActivePanelChange = (next: string | string[] | undefined) => {
    const keys = next === undefined ? [] : Array.isArray(next) ? next : [next];
    if (!keys.includes('worker-profile-update')) {
      setUpdateProfileWorkerId(null);
      setUpdateProfileTermId(null);
    }
    if (!keys.includes('worker-profile')) setProfileWorkerId(null);
    if (!keys.includes('shift-detail')) setSelectedShiftId(null);
  };

  return (
    // Fixed-height canvas below the app header: the session header stays pinned
    // at the top while the SideShell fills the rest and scrolls internally, so
    // the header never slides up under the fixed app header.
    <div className="schedule-review-canvas">
      <Flex vertical align="stretch" fullHeight>
        <ScheduleReviewSessionHeader
          serviceLineName={review?.serviceLineName ?? undefined}
          unitName={review?.unitName}
          assignmentsCount={autoFilledCount}
          isSaving={isSaving}
          // Hidden alongside the Activity tab while it runs on mock data (WORK-6016).
          onViewActivity={
            SHOW_ACTIVITY_TAB
              ? () => {
                  setActivitySourceFilter('system');
                  setActiveSideTab('activity');
                  setSideCollapsed(false);
                }
              : undefined
          }
          // Hidden when this unit can no longer be decided (read-only).
          onMarkComplete={canDecideUnit ? handleMarkComplete : undefined}
        />
        <SideShell
          collapsed={sideCollapsed}
          onCollapsedChange={setSideCollapsed}
          panels={sidePanels}
          activePanel={activePanelStack.length ? activePanelStack : undefined}
          onActivePanelChange={handleActivePanelChange}
          side={
            <Tabs
              activeKey={activeSideTab}
              onChange={setActiveSideTab}
              stickyHeader
            >
              <Tabs.Pane key="review" tab={<Tabs.Tab title="Review" />}>
                <Gutter asPadding size="md">
                  <Gap vertical size="lg" align="stretch">
                    {loading ? (
                      <>
                        <Placeholder
                          loading
                          title={{ width: '40%' }}
                          paragraph={{ rows: 3 }}
                        >
                          {null}
                        </Placeholder>
                        <Placeholder
                          loading
                          title={{ width: '55%' }}
                          paragraph={{ rows: 4 }}
                        >
                          {null}
                        </Placeholder>
                      </>
                    ) : review?.aiSummary ? (
                      <ScheduleAiSummary
                        key={aiSummaryStorageKey}
                        summary={review.aiSummary}
                        roles={roles}
                        storageKey={aiSummaryStorageKey}
                      />
                    ) : null}
                    <ScheduleNeedsAttention
                      items={activeWeek?.needsAttention}
                      weekKey={effectiveWeekKey}
                      weekLabel={activeWeek?.label}
                      onAction={handleNeedsAttentionAction}
                      shownScheduleKey={scheduleSelection?.key ?? null}
                      onDismiss={handleNeedsAttentionDismiss}
                    />
                  </Gap>
                </Gutter>
              </Tabs.Pane>
              <Tabs.Pane key="team" tab={<Tabs.Tab title="Team" />}>
                <ScheduleReviewTeamTab
                  members={members}
                  serviceLineName={review?.serviceLineName ?? undefined}
                  serviceLineUnits={review?.serviceLineUnits ?? []}
                  highlightedWorkerId={highlightedWorkerId}
                  onHighlightWorker={handleHighlightWorker}
                  assignedHighlightWorkerId={assignedHighlightWorkerId}
                  onHighlightAssigned={handleHighlightAssigned}
                  assignedWorkerIds={assignedWorkerIds}
                  eligibleWorkerIds={eligibleWorkerIds}
                  onWorkerSelect={handleWorkerSelect}
                  onWorkerDragStart={handleWorkerDragStart}
                  onWorkerDragEnd={handleWorkerDragEnd}
                  fteFilter={teamFteFilter}
                  onFteFilterChange={setTeamFteFilter}
                  workerIds={teamWorkerIds}
                  onWorkerIdsChange={setTeamWorkerIds}
                />
              </Tabs.Pane>
              {/* TODO(WORK-6016): hidden until the Activity feed runs on real data (see SHOW_ACTIVITY_TAB above). */}
              {SHOW_ACTIVITY_TAB && (
                <Tabs.Pane key="activity" tab={<Tabs.Tab title="Activity" />}>
                  <ScheduleReviewActivityTab
                    moves={MOCK_SCHEDULE_MOVES}
                    shiftsByWeek={MOCK_SHIFTS_BY_WEEK}
                    daysByWeek={MOCK_DAYS_BY_WEEK}
                    shiftTypeSections={MOCK_SHIFT_TYPE_SECTIONS}
                    selectedMoveId={selectedMoveId}
                    onMoveSelect={handleMoveSelect}
                    sourceFilter={activitySourceFilter}
                    onSourceFilterChange={setActivitySourceFilter}
                  />
                </Tabs.Pane>
              )}
            </Tabs>
          }
        >
          <ScheduleHealthStrip />
          <LoadingWrapper loading={loading}>
            {!loading && !review ? (
              <Gutter asPadding size="lg">
                <EmptyContent
                  header="Schedule unavailable"
                  text="We couldn't load this schedule. Check the link or try again."
                />
              </Gutter>
            ) : (
              <ScheduleCalendar
                shifts={effectiveShifts}
                demands={demands}
                shiftTypeSections={shiftTypeSections}
                roles={roles}
                days={days}
                weekStatuses={weekStatuses}
                activeWeekKey={effectiveWeekKey}
                onWeekSelect={setActiveWeekKey}
                onPreviousWeeks={
                  activeWeekIndex > 0 ? () => selectWeekByOffset(-1) : undefined
                }
                onNextWeeks={
                  activeWeekIndex < weeks.length - 1
                    ? () => selectWeekByOffset(1)
                    : undefined
                }
                roleFilter={roleFilter}
                onRoleFilterChange={setRoleFilter}
                shiftTypeFilter={shiftTypeFilter}
                onShiftTypeFilterChange={setShiftTypeFilter}
                highlightedShiftId={highlightedShiftId}
                availableShiftIds={availableShiftIds}
                assignedShiftIds={assignedShiftIds}
                attentionShiftIds={attentionShiftIds}
                onShiftClick={handleShiftSelect}
                dragEnabled={!isDragMutating && !isAssigning && !isUnassigning}
                onShiftDragStart={handleGridDragStart}
                onShiftDragEnd={handleWorkerDragEnd}
                onShiftDrop={handleDropAssign}
                getDropValidity={getDropValidity}
              />
            )}
          </LoadingWrapper>
        </SideShell>
      </Flex>
      <style jsx>{`
        .schedule-review-canvas {
          height: calc(100vh - var(--wk-header-height, 72px));
          overflow: hidden;
        }
        /* The session header keeps its natural height and stays pinned; the
           SideShell below it shrinks to fill the remaining space (it clips its
           own overflow and scrolls internally). */
        .schedule-review-canvas :global(.schedule-review-session-header) {
          flex-shrink: 0;
        }
      `}</style>
    </div>
  );
}
